import React, { useContext, useState } from 'react';
import Carousel from 'react-bootstrap/Carousel';
import Modal from 'react-bootstrap/Modal';
import { AppContext } from '../../App';
import { defaultImageUrl } from '../../config_and_helpers/config';
import {
  orderFileMediaKind,
  orderFileRowIsPendingUpload,
} from '../../utils/orderFiles';
import S3MediaImage from './S3MediaImage';
import S3MediaLink from './S3MediaLink';
import OrderFilePreviewIcon from '../Order/OrderFilePreviewIcon';
import { resolveMediaUrl } from '../../utils/s3MediaUrl';

const defaultImages = [
  { image_url: defaultImageUrl },
  { image_url: defaultImageUrl },
  { image_url: defaultImageUrl },
];

/**
 * Build media context for download-url from a carousel image row.
 *
 * @param {Object} image
 * @param {'productImageId'|'orderImageId'|null} imageIdKind
 * @returns {Object}
 */
function mediaContextForImage(image, imageIdKind) {
  if (!image) return {};
  if (imageIdKind === 'productImageId' && image.id != null) {
    return { productImageId: image.id };
  }
  if (imageIdKind === 'orderImageId' && image.id != null) {
    return { orderImageId: image.id };
  }
  return {
    productImageId: image.product_image_id,
    orderImageId: image.order_image_id,
    quoteId: image.quote_id,
    s3Key: image.s3_key,
  };
}

/**
 * Media kind for carousel slides. Product gallery rows are always images;
 * order file rows keep PDF/CSV/spreadsheet detection.
 *
 * @param {Object} image
 * @param {'productImageId'|'orderImageId'|null} imageIdKind
 * @param {'po'|'receipt'|null} orderFileCategory
 * @returns {'pdf'|'csv'|'spreadsheet'|'image'|'document'}
 */
function carouselMediaKind(image, imageIdKind, orderFileCategory) {
  if (imageIdKind === 'productImageId') {
    return 'image';
  }
  return orderFileMediaKind(image, { category: orderFileCategory });
}

/**
 * Carousel for product/order images with presigned S3 URL refresh.
 *
 * @param {Object} props
 * @param {Array<Object>} props.images - Rows with `image_url` and optional ids
 * @param {'productImageId'|'orderImageId'} [props.imageIdKind] - Maps `image.id` to download-url param
 * @param {'po'|'receipt'} [props.orderFileCategory] - PO rows may be PDF, CSV, or Excel
 */
const CarouselComponent = ({
  images,
  imageIdKind = null,
  orderFileCategory = null,
}) => {
  const { token } = useContext(AppContext);
  const [index, setIndex] = useState(0);
  const [modalShow, setModalShow] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');

  const handleSelect = selectedIndex => {
    setIndex(selectedIndex);
  };

  const handleImageClick = async (image, imageUrl) => {
    const ctx = mediaContextForImage(image, imageIdKind);
    const resolved = await resolveMediaUrl(token, imageUrl, ctx);
    setSelectedImageUrl(resolved);
    setModalShow(true);
  };

  const imagesToDisplay = images && images.length > 0 ? images : defaultImages;

  return (
    <>
      <Carousel
        activeIndex={index}
        onSelect={handleSelect}
        style={{ width: '40%', height: '40%', margin: 'auto' }}
      >
        {imagesToDisplay.map((image, idx) => {
          const ctx = mediaContextForImage(image, imageIdKind);
          const imageUrl = image.image_url;
          const mediaKind = carouselMediaKind(
            image,
            imageIdKind,
            orderFileCategory
          );
          const pending = orderFileRowIsPendingUpload(image);

          return (
            <Carousel.Item key={image.id ?? idx}>
              {pending || !imageUrl ? (
                <div className="d-flex justify-content-center align-items-center p-4 text-muted">
                  Uploading…
                </div>
              ) : mediaKind === 'image' ? (
                <button
                  type="button"
                  onClick={() => handleImageClick(image, imageUrl)}
                  style={{
                    cursor: 'pointer',
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    width: '100%',
                  }}
                >
                  <S3MediaImage
                    url={imageUrl}
                    {...ctx}
                    fallbackUrl={defaultImageUrl}
                    className="d-block w-100"
                    alt={`Slide ${idx}`}
                  />
                </button>
              ) : (
                <S3MediaLink
                  url={imageUrl}
                  {...ctx}
                  title="Open file"
                  className="w-100 d-flex justify-content-center align-items-center p-4 bg-light text-decoration-none"
                >
                  <OrderFilePreviewIcon
                    row={image}
                    category={orderFileCategory}
                    size={64}
                  />
                  <span className="ms-2 text-dark">Open file</span>
                </S3MediaLink>
              )}
            </Carousel.Item>
          );
        })}
      </Carousel>

      <Modal
        size="lg"
        show={modalShow}
        onHide={() => setModalShow(false)}
        centered
      >
        <Modal.Header closeButton />
        <Modal.Body>
          <img
            src={selectedImageUrl}
            alt="Enlarged view"
            className="w-100"
            style={{ cursor: 'pointer' }}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CarouselComponent;
